import { useDocumentDownload } from "@/components/Project/index.ts";
import {
    useDeleteProjectDocumentMutation,
    useUploadProjectDocumentMutation,
} from "@/mutations/project.ts";
import type { Project } from "@/queries/project.ts";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import UploadIcon from "@mui/icons-material/Upload";
import {
    Backdrop,
    CircularProgress,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemText,
    Menu,
    MenuItem,
} from "@mui/material";
import { fileOpen } from "browser-fs-access";
import { bindMenu, bindTrigger, usePopupState } from "material-ui-popup-state/hooks";
import type { ReactNode } from "react";

type Props = {
    label: string;
    project: Project;
    documentKey: keyof Project["documentObjectKeys"];
};

const DocumentListItem = ({ label, project, documentKey }: Props): ReactNode => {
    const uploadMutation = useUploadProjectDocumentMutation();
    const deleteMutation = useDeleteProjectDocumentMutation();
    const { downloading, startDownload } = useDocumentDownload(project.id, documentKey);
    const menuState = usePopupState({ variant: "popover", popupId: `document-${documentKey}` });
    const objectKey = project.documentObjectKeys[documentKey];

    const handleDocumentSelect = (data: Blob, contentType: string) => {
        uploadMutation.mutate({
            projectId: project.id,
            contentType,
            data,
            documentKey,
        });
    };

    const selectDocument = async () => {
        const document = await fileOpen({
            mimeTypes: [
                "application/pdf",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ],
        });
        const reader = new FileReader();

        reader.addEventListener("load", () => {
            handleDocumentSelect(new Blob([reader.result as ArrayBuffer]), document.type);
        });

        reader.readAsArrayBuffer(document);
    };

    const isLoading = uploadMutation.isPending || deleteMutation.isPending || downloading;

    if (!objectKey) {
        return (
            <ListItem
                secondaryAction={
                    <IconButton
                        onClick={() => {
                            void selectDocument();
                        }}
                    >
                        <UploadIcon />
                    </IconButton>
                }
                disableGutters
            >
                <ListItemText primary={label} />
                <Backdrop open={isLoading}>
                    <CircularProgress />
                </Backdrop>
            </ListItem>
        );
    }

    return (
        <ListItem
            secondaryAction={
                <>
                    <IconButton {...bindTrigger(menuState)}>
                        <MoreVertIcon />
                    </IconButton>

                    <Menu {...bindMenu(menuState)}>
                        <MenuItem
                            onClick={() => {
                                menuState.close();
                                void selectDocument();
                            }}
                        >
                            Replace
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                menuState.close();
                                deleteMutation.mutate({
                                    projectId: project.id,
                                    documentKey,
                                });
                            }}
                        >
                            Delete
                        </MenuItem>
                    </Menu>
                </>
            }
            disableGutters
            disablePadding
        >
            <ListItemButton disableGutters onClick={startDownload}>
                <ListItemText primary={label} />
            </ListItemButton>

            <Backdrop open={isLoading}>
                <CircularProgress />
            </Backdrop>
        </ListItem>
    );
};

export default DocumentListItem;
